package fr.frux.core.designsystem.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.resources.Res
import fr.frux.core.designsystem.resources.ds_needs_check_badge
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.color
import org.jetbrains.compose.resources.stringResource

@Composable
fun TreeCard(
    name: String,
    meta: String,
    status: FruxStatus,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    fruitEmoji: String? = null,
    glyph: ImageVector? = null,
    /** Teinte du marqueur de cet arbre sur la carte (`treeMarkerTint`) ; repli sur la couleur du statut. */
    glyphTint: Color? = null,
    thumbUrl: String? = null,
    needsCheck: Boolean = false,
    /** Mention de passage humain (#420, ADR 0025), sous [meta] ; absente des cartes qui ne la portent pas. */
    sightingLabel: String? = null,
    trailing: (@Composable () -> Unit)? = null,
) {
    FruxCard(modifier = modifier.fillMaxWidth(), onClick = onClick) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TreeCardEmoji(fruitEmoji, glyph, glyphTint ?: status.color(), thumbUrl)
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = name,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = meta,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                if (!sightingLabel.isNullOrBlank()) {
                    Text(
                        text = sightingLabel,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
            Box(modifier = Modifier.width(FruxSpacing.sm))
            if (trailing != null) {
                trailing()
            } else {
                Column(horizontalAlignment = Alignment.End) {
                    // Surface de décision de cueillette (#143) : même signal « à vérifier » que la
                    // carte (contour crénelé) et la fiche (« Identification à confirmer »), traduit
                    // ici en pastille neutre — jamais de couleur d'alerte, registre imposé par
                    // l'ADR 0015.
                    if (needsCheck) {
                        NeedsCheckBadge()
                        Box(modifier = Modifier.height(4.dp))
                    }
                    StatusBadge(status)
                }
            }
        }
    }
}

/** Pastille « à vérifier » (#143) : même motif que [StatusBadge], teinte neutre volontaire. */
@Composable
private fun NeedsCheckBadge() {
    Text(
        text = stringResource(Res.string.ds_needs_check_badge),
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier =
            Modifier
                .clip(RoundedCornerShape(50))
                .background(MaterialTheme.colorScheme.surfaceVariant)
                .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

/**
 * Pastille de tête : quand [thumbUrl] est fourni, la vraie vignette héro remplace la cascade
 * emoji/glyphe/icône, gardée comme repli le temps du chargement ([FruxRemoteImage]). Sinon,
 * `fruitEmoji` prime toujours (compat existante). À défaut, un [glyph] espèce (#198, `glyphFor`)
 * s'affiche comme le marqueur de la carte ([TreeGlyphDisc] : plaque a la teinte du marqueur, glyphe blanc) —
 * repli sur l'icône `Park`/`primaryContainer` uniquement si aucun des trois n'est fourni.
 */
@Composable
private fun TreeCardEmoji(
    fruitEmoji: String?,
    glyph: ImageVector?,
    glyphTint: Color,
    thumbUrl: String? = null,
) {
    val backgroundColor =
        if (glyph != null && fruitEmoji == null) {
            glyphTint
        } else {
            MaterialTheme.colorScheme.primaryContainer
        }
    Box(
        modifier =
            Modifier
                .size(TreeGlyphDiscSize)
                .clip(CircleShape)
                .background(backgroundColor, CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        val fallback: @Composable () -> Unit = {
            when {
                fruitEmoji != null -> {
                    Text(text = fruitEmoji, style = MaterialTheme.typography.titleMedium)
                }

                glyph != null -> {
                    TreeGlyphDisc(glyph = glyph, tint = glyphTint)
                }

                else -> {
                    Icon(
                        imageVector = Icons.Outlined.Park,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary,
                    )
                }
            }
        }
        if (thumbUrl != null) {
            FruxRemoteImage(
                url = thumbUrl,
                contentDescription = null,
                modifier = Modifier.matchParentSize(),
                fallback = fallback,
            )
        } else {
            fallback()
        }
    }
    Box(modifier = Modifier.width(FruxSpacing.md))
}
